import RelevanceScoreBadge from "./RelevanceScoreBadge";
import OpportunityHighlightsList from "./OpportunityHighlightsList";
import OpportunityExpandedContent from "./OpportunityExpandedContent";
import {
    getCardSurfaceClass,
    getOpportunityBody,
    getOpportunityHighlights,
} from "../../../utils/opportunityCard";

export default function OpportunityCardCompact({
    opportunity,
    className = "",
    selectable = false,
    isSelected = false,
    disabled = false,
    onToggle,
    showActions = false,
    isExpanded = false,
    onExpandedChange,
}) {
    const {
        title,
        relevanceScore,
        engagementAngles = [],
        contentAngles = [],
        hashtags = [],
        eventName,
        opportunity: opportunityText,
    } = opportunity ?? {};

    const body = getOpportunityBody(opportunity);
    const displayTitle = String(title ?? "").trim();
    const showExpanded = showActions ? isExpanded : false;
    const highlights = getOpportunityHighlights({ engagementAngles, contentAngles });
    const canExpand =
        Boolean(body) ||
        highlights.length > 0 ||
        hashtags.length > 0 ||
        Boolean(String(eventName ?? "").trim()) ||
        Boolean(String(opportunityText ?? "").trim());

    const canToggleSelect = selectable && Boolean(onToggle) && !(disabled && !isSelected);

    const toggleSelect = () => {
        if (!canToggleSelect) return;
        onToggle();
    };

    const handleSelectClick = (event) => {
        event.stopPropagation();
        toggleSelect();
    };

    const handleExpandClick = (event) => {
        event.stopPropagation();
        onExpandedChange?.(!isExpanded);
    };

    const handleCardClick = () => {
        toggleSelect();
    };

    return (
        <article
            onClick={canToggleSelect ? handleCardClick : undefined}
            className={[
                "group relative flex w-full min-w-0 flex-col overflow-hidden rounded-2xl border px-4 py-4 text-left transition-shadow duration-200",
                showExpanded ? "shadow-md" : "h-full shadow-sm hover:shadow-md",
                canToggleSelect ? "cursor-pointer" : "",
                getCardSurfaceClass(relevanceScore, isSelected),
                className,
            ]
                .filter(Boolean)
                .join(" ")}
        >
            <div className={`space-y-3 ${showExpanded ? "w-full" : ""}`}>
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1 space-y-2">
                        {displayTitle ? (
                            <h3
                                className={`mb-0 font-semibold leading-snug tracking-tight text-[#021645] ${
                                    showExpanded ? "text-base md:text-lg" : "line-clamp-2 text-[0.95rem]"
                                }`}
                            >
                                {displayTitle}
                            </h3>
                        ) : null}

                        {body ? (
                            <p
                                className={`mb-0 leading-relaxed text-slate-600 ${
                                    showExpanded
                                        ? "text-sm md:text-[0.95rem]"
                                        : "line-clamp-3 text-xs text-slate-500"
                                }`}
                            >
                                {body}
                            </p>
                        ) : null}
                    </div>

                    {!showExpanded ? (
                        <RelevanceScoreBadge score={relevanceScore} className="mt-0.5" />
                    ) : null}
                </div>

                {showExpanded ? (
                    <div className="space-y-3">
                        {relevanceScore != null ? (
                            <div className="border-t border-slate-100 pt-3">
                                <RelevanceScoreBadge score={relevanceScore} align="left" />
                            </div>
                        ) : null}

                        <OpportunityExpandedContent
                            engagementAngles={engagementAngles}
                            contentAngles={contentAngles}
                            hashtags={hashtags}
                            eventName={eventName}
                            opportunity={
                                String(opportunityText ?? "").trim() !== String(body ?? "").trim()
                                    ? opportunityText
                                    : ""
                            }
                        />
                    </div>
                ) : highlights.length > 0 ? (
                    <div className="space-y-1.5 border-t border-slate-100/80 pt-2.5">
                        <p className="mb-0 text-[10px] font-bold uppercase tracking-[0.14em] text-amber-700/90">
                            Highlights
                        </p>
                        <OpportunityHighlightsList highlights={highlights} maxItems={2} />
                    </div>
                ) : null}
            </div>

            {showActions ? (
                <div
                    className={`flex w-full shrink-0 items-center justify-between gap-3 border-t border-slate-100 pt-3 ${
                        showExpanded ? "mt-4" : "mt-auto"
                    }`}
                >
                    <button
                        type="button"
                        onClick={handleSelectClick}
                        disabled={disabled && !isSelected}
                        aria-pressed={isSelected}
                        className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-semibold shadow-sm transition ${
                            isSelected
                                ? "bg-emerald-600 text-white hover:bg-emerald-700"
                                : "bg-orange-700 text-white hover:bg-orange-800 disabled:cursor-not-allowed disabled:opacity-40"
                        }`}
                    >
                        <i
                            className={`bi ${isSelected ? "bi-check2" : "bi-arrow-right"} text-sm leading-none`}
                            aria-hidden
                        />
                        {isSelected ? "Using This Trend" : "Use This Trend"}
                    </button>

                    {canExpand ? (
                        <button
                            type="button"
                            onClick={handleExpandClick}
                            aria-expanded={isExpanded}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-violet-700 transition hover:text-violet-900"
                        >
                            {isExpanded ? "Show less" : "View more"}
                            <i
                                className={`bi ${isExpanded ? "bi-arrow-up" : "bi-arrow-right"} text-sm leading-none`}
                                aria-hidden
                            />
                        </button>
                    ) : null}
                </div>
            ) : null}
        </article>
    );
}
